import React, { useState } from "react";
import { Layer, Source } from "react-map-gl";
import { FeatureCardComponent } from "../cards/feature-card";
import { InfoCard } from "../cards/info-card";
import {
  ffeBuildingFFEStyle,
  ffeBuildingHeightStyle,
  ffeCadastreStyle,
  modelBuildingFFEStyle,
  modelBuildingHeightStyle,
  modelCadastreStyle,
  modelFloodStyle,
  realBuildingFFEStyle,
  realBuildingHeightStyle,
  realCadastreStyle,
  realFloodStyle,
} from "./styles";
import { FormState, Mode, StepDemoConfig, GeoJsonDemoMapLayer } from "./types";
import { createCoordinates } from "./utils";

type Layer = "building_ffe" | "building_height" | "cadastre" | "flood";

const styleForLayer = (mode: Mode, layer: Layer, selectedId: string | null) => {
  switch (mode) {
    case "ffe": {
      switch (layer) {
        case "building_ffe": {
          return ffeBuildingFFEStyle(selectedId);
        }
        case "cadastre": {
          return ffeCadastreStyle();
        }
        case "building_height": {
          return ffeBuildingHeightStyle(selectedId);
        }
        case "flood": {
          return realFloodStyle();
        }
      }
    }
    case "real": {
      switch (layer) {
        case "building_ffe": {
          return realBuildingFFEStyle(selectedId);
        }
        case "cadastre": {
          return realCadastreStyle();
        }
        case "building_height": {
          return realBuildingHeightStyle(selectedId);
        }
        case "flood": {
          return realFloodStyle();
        }
      }
    }
    default: {
      switch (layer) {
        case "building_ffe": {
          return modelBuildingFFEStyle(mode, selectedId);
        }
        case "cadastre": {
          return modelCadastreStyle(mode);
        }
        case "building_height": {
          return modelBuildingHeightStyle(mode, selectedId);
        }
        case "flood": {
          return modelFloodStyle();
        }
      }
    }
  }
};

interface MapLayerProps {
  geojsonPath: string;
  formState: FormState;
  layer_name: string;
  highlightedLayer: any;
  style: object;
  beforeId?: string;
}

export const MapLayer: React.FC<MapLayerProps> = React.memo(
  ({ geojsonPath, layer_name, style, beforeId }) => {
    const [, setGeoJsonData] = useState<any>(null);
    const [, setError] = useState<string | null>(null);

    // Preserve state setters to avoid breaking the existing pattern
    void setGeoJsonData;
    void setError;

    return (
      <Source type="geojson" data={geojsonPath}>
        {/* @ts-ignore */}
        <Layer {...style} id={layer_name} beforeId={beforeId} />
      </Source>
    );
  },
);

const createGeojsonLayers = (showLayers: boolean): GeoJsonDemoMapLayer[] => {
  if (!showLayers) return [];

  return [
    {
      layerName: "flood-cadastre",
      geojsonPath: "/demos/geojson/flood_hub_demo_cadastre_ingham.json",
      createStyle: (formState: FormState, selectedId: string | null) => {
        const { mode } = formState;
        return {
          id: "flood-cadastre",
          type: "fill-extrusion",
          layout: {
            visibility: mode == Mode.ffe ? "none" : "visible",
          },
          paint:
            mode == Mode.ffe
              ? {}
              : styleForLayer(mode, "cadastre", selectedId),
        };
      },
    },
    {
      layerName: "cadastre-layer",
      geojsonPath: "/demos/geojson/flood_hub_demo_cadastre_ingham.json",
      createStyle: (formState: FormState, selectedId: string | null) => {
        const { mode } = formState;
        return {
          id: "cadastre-layer",
          type: "line",
          layout: {
            visibility: mode == Mode.ffe ? "visible" : "none",
          },
          paint:
            mode == Mode.ffe
              ? styleForLayer(mode, "cadastre", selectedId)
              : {},
        };
      },
    },
    {
      layerName: "flood-building-ffe",
      geojsonPath: "/demos/geojson/flood_hub_demo_buildings_ingham.json",
      createStyle: (formState: FormState, selectedId: string | null) => {
        const { mode } = formState;
        return {
          id: "flood-buildings-ffe",
          type: "fill-extrusion",
          paint: styleForLayer(mode, "building_ffe", selectedId),
        };
      },
    },
    {
      layerName: "flood-building-height",
      geojsonPath: "/demos/geojson/flood_hub_demo_buildings_ingham.json",
      createStyle: (formState: FormState, selectedId: string | null) => {
        const { mode } = formState;
        return {
          id: "flood-building-height",
          type: "fill-extrusion",
          paint: styleForLayer(mode, "building_height", selectedId),
        };
      },
    },
    {
      layerName: "flood-level",
      geojsonPath: "/demos/geojson/flood_hub_demo_aoi_ingham.json",
      createStyle: (formState: FormState, selectedId: string | null) => {
        const { mode } = formState;
        return {
          id: "flood-level",
          type: "fill-extrusion",
          layout: {
            visibility: mode == Mode.real ? "visible" : "none",
          },
          paint: styleForLayer(mode, "flood", selectedId),
        };
      },
    },
  ];
};

export const stepsDemos: Record<number, StepDemoConfig> = {
  1: {
    id: 1,
    component: InfoCard,
    mapConfig: { zoomLevel: 4 },
    data: {
      title: "Ground-Level Flood Models: A Limited View",
      copy: (
        <div className="space-y-2">
          <p>
            Traditional flood models only reveal flooding at ground level. By
            incorporating First Floor Elevation (FFE) data—the height of the
            first habitable floor above the ground—the true extent of a
            flooding event is uncovered at a building level, ensuring the right
            approach to flood management is applied.
          </p>
          <p>
            Let's see how this works with an example from Ingham, QLD, a
            community with a long history of flooding.
          </p>
        </div>
      ),
    },
    coordinates: createCoordinates(-25, 134),
    layer: <></>,
  },
  2: {
    id: 2,
    component: InfoCard,
    mapConfig: { zoomLevel: 13, pitch: 60, bearing: 30 },
    data: {
      title: "Ingham's Flood History",
      copy: (
        <div className="space-y-2">
          <p>
            Ingham has endured some of Australia's most significant flooding
            events: the devastating Black Thursday flood in 1927, record river
            heights in 1967, and more recent floods in 2009, 2018, 2023, and
            2025.
          </p>
          <p>
            Traditional ground-level models fall short in such volatile
            regions, failing to capture the individual building impacts of
            flooding.
          </p>
        </div>
      ),
    },
    coordinates: createCoordinates(-18.65144, 146.16271),
    layer: <></>,
    geojsonLayers: createGeojsonLayers(true),
  },
  3: {
    id: 3,
    component: InfoCard,
    mapConfig: { zoomLevel: 19, pitch: 60, bearing: 30 },
    data: {
      title: "Visualising Buildings with Elevation Data",
      copy: (
        <div className="space-y-2">
          <p>
            Here, we start by visualising buildings in Ingham with First Floor
            Elevation (FFE) information.
          </p>
          <ul className="ml-5 list-disc space-y-1">
            <li>
              <strong className="text-purple-600">Purple</strong> bases represent
              buildings with FFE data
            </li>
            <li>
              <strong>Black</strong> outlines show cadastral parcels
            </li>
          </ul>
        </div>
      ),
    },
    coordinates: createCoordinates(-18.65488, 146.16271),
    layer: <></>,
    geojsonLayers: createGeojsonLayers(true),
  },
  4: {
    id: 4,
    component: InfoCard,
    mapConfig: { zoomLevel: 19, pitch: 60, bearing: 30 },
    data: {
      title: "Visualising the Real Impact of Floods",
      copy: (
        <div className="space-y-2">
          <p>
            When we overlay the February 2025 flood data, the true impact
            becomes clear:
          </p>
          <ul className="ml-5 list-disc space-y-1">
            <li>
              <strong className="text-destructive">Red</strong> = deeply flooded
            </li>
          </ul>
          <p>More importantly, some buildings were not impacted:</p>
          <ul className="ml-5 list-disc space-y-1">
            <li>
              <strong className="text-success">Green</strong> = buildings
              outside the flood area
            </li>
            <li>
              <strong className="text-purple-600">Purple</strong> = buildings
              elevated above flood level
            </li>
          </ul>
        </div>
      ),
    },
    coordinates: createCoordinates(-18.65488, 146.16271),
    layer: <></>,
    geojsonLayers: createGeojsonLayers(true),
  },
  5: {
    id: 5,
    component: InfoCard,
    mapConfig: { zoomLevel: 19, pitch: 60, bearing: 30 },
    data: {
      title: "The Difference First Floor Elevation Makes",
      copy: (
        <div className="space-y-2">
          <p>
            Using traditional ground-level data, 1,150 properties appeared
            inundated.
          </p>
          <p>
            With precise FFE data, 430 properties—38%—were actually above flood
            level and safe.
          </p>
          <p>
            This variance dramatically influences risk models, emergency
            response, and community resilience planning.
          </p>
        </div>
      ),
    },
    coordinates: createCoordinates(-18.65488, 146.16271),
    layer: <></>,
    geojsonLayers: createGeojsonLayers(true),
  },
  6: {
    id: 6,
    component: InfoCard,
    mapConfig: { zoomLevel: 19, pitch: 60, bearing: 30 },
    data: {
      title: "Common Flood Events",
      copy: (
        <div className="space-y-2">
          <p>
            <strong>Modelling a 1-in-5-Year Scenario</strong>
          </p>
          <p>
            The model isn't just for historic events. We can also simulate more
            frequent, lower-intensity floods.
          </p>
          <p>
            The map now shows a 1-in-5-year flood event. Notice how the impact
            is less widespread, but the same principles apply. Even in these
            common scenarios, understanding which specific buildings are
            vulnerable is key to effective mitigation.
          </p>
        </div>
      ),
    },
    coordinates: createCoordinates(-18.65488, 146.16271),
    layer: <></>,
    geojsonLayers: createGeojsonLayers(true),
  },
  7: {
    id: 7,
    component: InfoCard,
    mapConfig: { zoomLevel: 19, pitch: 60, bearing: 30 },
    data: {
      title: "Extreme Weather Scenarios",
      copy: (
        <div className="space-y-2">
          <p>
            <strong>Preparing for a 1-in-500-Year Event</strong>
          </p>
          <p>Now, let's look at a more extreme scenario.</p>
          <p>
            The map is displaying a 1-in-500-year flood event, a rare but
            catastrophic possibility. Visualising this level of risk helps
            infrastructure planners and emergency services prepare for
            worst-case scenarios with greater accuracy and confidence.
          </p>
        </div>
      ),
    },
    coordinates: createCoordinates(-18.65488, 146.16271),
    layer: <></>,
    geojsonLayers: createGeojsonLayers(true),
  },
  8: {
    id: 8,
    component: InfoCard,
    mapConfig: { zoomLevel: 19, pitch: 60, bearing: 30 },
    data: {
      title: "You're in Control",
      copy: (
        <div className="space-y-2">
          <p>
            <strong>Explore Different Scenarios</strong>
          </p>
          <p>You now have control over the flood model.</p>
          <p>
            On the left-hand side of your screen, you will see a dropdown menu.
            Use this to switch between the different flood scenarios you've
            just seen:
          </p>
          <ul className="ml-5 list-disc space-y-1">
            <li>February 2025 Flood Event</li>
            <li>1-in-2-Year Flood Event</li>
            <li>1-in-500-Year Flood Event</li>
          </ul>
          <p>
            Feel free to switch between them to compare their different impacts
            on the Ingham community.
          </p>
        </div>
      ),
    },
    coordinates: createCoordinates(-18.65488, 146.16271),
    layer: <></>,
    geojsonLayers: createGeojsonLayers(true),
  },
  9: {
    id: 9,
    component: InfoCard,
    mapConfig: { zoomLevel: 19, pitch: 60, bearing: 30 },
    data: {
      title: "Why First Floor Elevation Matters",
      copy: (
        <div className="space-y-2">
          <p>
            FFE data enables accurate identification and quantification of
            flood-affected buildings across multiple scenarios—ideal for:
          </p>
          <ul className="ml-5 list-disc space-y-1">
            <li>Risk modelling</li>
            <li>Prioritising evacuation zones</li>
            <li>Development planning…and more.</li>
          </ul>
        </div>
      ),
    },
    coordinates: createCoordinates(-18.65488, 146.16271),
    layer: <></>,
    geojsonLayers: createGeojsonLayers(true),
  },
  10: {
    id: 10,
    component: InfoCard,
    mapConfig: { zoomLevel: 19, pitch: 60, bearing: 30 },
    data: {
      title: "Explore on Your Own",
      copy: (
        <div className="space-y-2">
          <p>Your Turn to Discover</p>
          <p>
            You now have all the tools to explore the power of First Floor
            Elevation Insights.
          </p>
          <p>
            Change scenarios, select individual properties, and see for
            yourself how this next-generation spatial data provides a clearer,
            more accurate understanding of flood risk.
          </p>
          <p>Ready to learn more?</p>
          <p>
            If you'd like to see how Geoscape FFE Insights can transform risk
            assessment for your organisation, click the button below, and one
            of our specialists will be in touch.
          </p>
        </div>
      ),
      showExploreButton: true,
      showSpeakToExpertButton: true,
    },
    coordinates: createCoordinates(-18.65488, 146.16271),
    layer: <></>,
    geojsonLayers: createGeojsonLayers(true),
  },
  11: {
    id: 11,
    component: FeatureCardComponent,
    mapConfig: { zoomLevel: 13, pitch: 60, bearing: 30 },
    data: {},
    coordinates: createCoordinates(-18.65488, 146.16271),
    layer: <></>,
    geojsonLayers: createGeojsonLayers(true),
  },
};
